import type { EMediaType } from "@frontend-shared/types";
import { useContext, useMemo } from "react";

import type {
  IActiveMediaDownload,
  IMediaDownloadContext,
  IStartMediaDownloadProps,
} from "@/contexts/MediaDownloadContext";
import { MediaDownloadContext } from "@/contexts/MediaDownloadContext";

export interface IUseMediaDownloadResult {
  downloadingType: EMediaType | null;
  downloadProgress: number;
  startDownload: (props: IStartMediaDownloadProps) => void;
  cancelDownload: (meetingUuid: string) => void;
}

export interface IUseMediaDownloadManagerResult {
  activeDownloads: IActiveMediaDownload[];
  cancelDownload: (meetingUuid: string) => void;
}

const useMediaDownloadContext = (): IMediaDownloadContext => {
  const context = useContext(MediaDownloadContext);

  // NOTE: this guard ensures the hook is used within its required provider and does not need test coverage.
  /* istanbul ignore next */
  if (context === null) {
    throw new Error(
      "useMediaDownload must be used within a MediaDownloadProvider",
    );
  }

  return context;
};

// NOTE: Reads the download slice for a single meeting. The slice persists across
// navigation, so the play bar shows the correct in-flight progress after the
// user leaves and returns.
export const useMediaDownload = (
  meetingUuid: string,
): IUseMediaDownloadResult => {
  const { downloads, startDownload, cancelDownload } =
    useMediaDownloadContext();
  const downloadingType = downloads[meetingUuid]?.downloadingType ?? null;
  const downloadProgress = downloads[meetingUuid]?.downloadProgress ?? 0;

  return useMemo(
    () => ({
      downloadingType,
      downloadProgress,
      startDownload,
      cancelDownload,
    }),
    [downloadingType, downloadProgress, startDownload, cancelDownload],
  );
};

// NOTE: Exposes the bulk clear action so the authenticated tree can purge
// in-flight downloads on logout / account switch (see MediaDownloadAuthSync).
export const useMediaDownloadReset = (): (() => void) => {
  return useMediaDownloadContext().clearAll;
};

// NOTE: Reads every active download plus the cancel action for the app-level
// DownloadIndicator, which is visible on any page while a download runs.
export const useMediaDownloadManager = (): IUseMediaDownloadManagerResult => {
  const { downloads, cancelDownload } = useMediaDownloadContext();

  const activeDownloads = useMemo(
    (): IActiveMediaDownload[] =>
      Object.entries(downloads).map(([meetingUuid, state]) => ({
        meetingUuid,
        ...state,
      })),
    [downloads],
  );

  return useMemo(
    () => ({ activeDownloads, cancelDownload }),
    [activeDownloads, cancelDownload],
  );
};
